import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FirestoreDataService } from '../core/firestore-data.service';
import { WorkshopWork, WorkStatus, WorkCurrentStatus } from '../core/models';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-tracking',
  templateUrl: './tracking.component.html',
  styleUrls: ['./tracking.component.scss'],
  standalone: false
})
export class TrackingComponent implements OnInit {
  caseNumber: string | null = null;
  work$: Observable<WorkshopWork | null>;
  status$: Observable<WorkStatus | null> = of(null);
  
  isLoading = true;
  error = false;

  // Definición de pasos para la línea de tiempo
  steps = [
    { status: 'Received', label: 'Recibida', icon: 'bi-box-arrow-in-right' },
    { status: 'In Progress', label: 'En Proceso', icon: 'bi-gear-fill' },
    { status: 'Ready', label: 'Lista', icon: 'bi-check-circle-fill' },
    { status: 'Completed', label: 'Entregada', icon: 'bi-bicycle' }
  ];

  constructor(
    private route: ActivatedRoute,
    private firestoreService: FirestoreDataService
  ) {
    this.caseNumber = this.route.snapshot.paramMap.get('caseNumber');
    
    this.work$ = of(this.caseNumber).pipe(
      switchMap(num => num ? this.firestoreService.getWorkByCaseNumber(num) : of(null)),
      catchError(() => {
        this.error = true;
        this.isLoading = false;
        return of(null);
      })
    );
  }

  ngOnInit(): void {
    this.work$.subscribe(work => {
      this.isLoading = false;
      if (work && work.id) {
        this.status$ = this.firestoreService.getWorkStatus(work.id);
      } else {
        this.error = true;
      }
    });
  }

    getSpanishStatus(status: WorkCurrentStatus | undefined): string {
    if (!status) return 'Desconocido';
    const statusMap: Record<string, string> = {
      'Received': 'Recibido',
      'In Progress': 'En Progreso',
      'Awaiting Parts': 'Esperando Repuestos',
      'Ready': 'Lista para Entrega',
      'Completed': 'Entregada',
      'Cancelled': 'Cancelada'
    };
    return statusMap[status] || status;
  }


  // Lógica para determinar si un paso está completado, activo o pendiente
  getStepClass(stepStatus: string, currentStatus: WorkCurrentStatus | undefined): string {
    if (!currentStatus) return 'pending';
    
    const statusOrder: WorkCurrentStatus[] = ['Received', 'In Progress', 'Awaiting Parts', 'Ready', 'Completed'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    // Si el estado es "Esperando Repuestos", lo tratamos como "En Proceso" para el cliente
    const normalizedStepStatus = stepStatus === 'In Progress' && currentStatus === 'Awaiting Parts' ? currentStatus : stepStatus;
    const stepIndex = statusOrder.indexOf(normalizedStepStatus as WorkCurrentStatus);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    if (currentStatus === 'Awaiting Parts' && stepStatus === 'In Progress') return 'active';
    
    return 'pending';
  }

  getStatusMessage(status: WorkCurrentStatus | undefined): string {
    switch (status) {
      case 'Received': return 'Tu bicicleta ha ingresado al taller y está en cola para revisión.';
      case 'In Progress': return 'Nuestros mecánicos están trabajando en tu bicicleta.';
      case 'Awaiting Parts': return 'Estamos esperando repuestos para continuar con el servicio.';
      case 'Ready': return '¡Buenas noticias! Tu bicicleta ya está lista para ser retirada.';
      case 'Completed': return 'El servicio ha finalizado y la bicicleta fue entregada.';
      case 'Cancelled': return 'Esta orden de trabajo ha sido cancelada.';
      default: return 'Cargando estado...';
    }
  }
}